/**
 * The database objects the SQL completion of the application offers: the static catalogue of the repository
 * (SQL_SCHEMA_CATALOG of @reporting/rp-utils-sql-completion) with the live objects of the customer databases
 * merged in. The live objects come from rp-ms-etl (GET schema/catalog: the data and ETL databases of the company,
 * see SchemaRoute.php) and are fetched once per company and session, on the first completion request of a SQL
 * editor; an editor starts with the static catalogue and picks the live one up on the next request once it
 * arrived (the provider reads the catalogue through a function, see SqlSchemaOptions.catalog).
 *
 * The objects are not checked for changes afterwards: after a change of the database the user asks for them
 * again through the gear menu of the editor (refreshSqlSchemaCatalog()), which sends the version the server gave
 * along and gets the objects only when something changed. A failed request (no right to the endpoint, the service
 * down) keeps the static catalogue and is tried again after RETRY_AFTER.
 */
import { log } from "@omegagrid/core";
import { AccountingRights } from "@reporting/rp-types/core";
import { mergeSchemaCatalog, SQL_SCHEMA_CATALOG, SqlDatabaseRole, SqlSchemaCatalog, SqlSchemaDatabase } from "@reporting/rp-utils-sql-completion";
import rp from "../global";

/** the answer of GET schema/catalog of rp-ms-etl */
export type SqlSchemaCatalogResponse = {
	version: string,
	/** null when the version sent along is still the current one */
	databases: Partial<Record<SqlDatabaseRole, SqlSchemaDatabase>> | null,
};

/** the rights the endpoint is registered with (any of them opens it), see SchemaRoute.php */
const CATALOG_RIGHTS: string[] = [
	AccountingRights.ETL,
	AccountingRights.JobsRead,
	AccountingRights.JobsDefine,
	AccountingRights.DWHRead,
	AccountingRights.DWHWrite,
	AccountingRights.Custom,
	AccountingRights.CustomDefine,
];
const RETRY_AFTER = 60 * 1000;

type CatalogEntry = {
	catalog: SqlSchemaCatalog,
	/** the version of the catalogue the server gave; null while only the static one is known */
	version: string | null,
	/** when the last request failed; 0 after a success */
	failedAt: number,
	loading: Promise<boolean> | null,
};

/** per company (accounting id) */
const entries = new Map<number, CatalogEntry>();

/** dispatches 'change' when the live catalogue of a company arrived or changed */
export const sqlSchemaCatalogEvents = new EventTarget();

/**
 * A reader of the catalogue of the company the host is in, for SqlSchemaOptions.catalog of the completion: the
 * static catalogue until the live objects arrive, which the first read asks the server for.
 */
export function getSqlSchemaCatalogSource(): () => SqlSchemaCatalog {
	return () => entry(rp.accountingProvider?.a, true).catalog;
}

/** The catalogue of the moment without asking the server (the static one until a fetch happened). */
export function getSqlSchemaCatalog(): SqlSchemaCatalog {
	return entry(rp.accountingProvider?.a, false).catalog;
}

/** Asks the server for the objects of the company again (after a change of the database); false when it could not. */
export async function refreshSqlSchemaCatalog(): Promise<boolean> {
	const a = rp.accountingProvider?.a;
	const current = entry(a, false);
	if (current.loading) await current.loading;
	return load(a, current);
}

/** Forgets the fetched catalogues (a sign-out). */
export function clearSqlSchemaCatalog(): void {
	entries.clear();
}

function entry(a: number | null | undefined, fetch: boolean): CatalogEntry {
	const key = a ?? 0;
	let current = entries.get(key);
	if (!current) entries.set(key, current = { catalog: SQL_SCHEMA_CATALOG, version: null, failedAt: 0, loading: null });
	// not fetched yet (or the fetch failed a while ago): ask in the background, the static catalogue serves meanwhile
	if (fetch && !current.loading && current.version === null && Date.now() - current.failedAt > RETRY_AFTER) void load(a, current);
	return current;
}

function load(a: number | null | undefined, current: CatalogEntry): Promise<boolean> {
	if (current.loading) return current.loading;
	const service = rp.ms?.('etl');
	// nothing to ask: no company, no ETL service in this application, or no right to the endpoint (a 401 would only make noise)
	if (!a || !service || !rp.userProvider?.hasRight(CATALOG_RIGHTS)) {
		current.failedAt = Date.now();
		return Promise.resolve(false);
	}
	current.loading = (async () => {
		try {
			// the version already held: the server leaves the databases out when nothing changed since
			const params = current.version ? { version: current.version } : undefined;
			const response = await service.get<SqlSchemaCatalogResponse>('schema/catalog', params, { showAlerts: false, throwException: true });
			if (!response?.version) return false;
			if (response.databases) {
				current.catalog = mergeSchemaCatalog(SQL_SCHEMA_CATALOG, response.databases);
				sqlSchemaCatalogEvents.dispatchEvent(new Event('change'));
			}
			current.version = response.version;
			current.failedAt = 0;
			return true;
		} catch (error) {
			log.getLogger('rp').warn(`SQL schema catalogue: ${error}`);
			current.failedAt = Date.now();
			return false;
		} finally {
			current.loading = null;
		}
	})();
	return current.loading;
}
